import {Badge} from "../../../basic/badge.tsx";
import {Package} from "lucide-react";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {ColumnDef, DataTable} from "../../../shared/tables/DataTable.tsx";
import {VwpLicense} from "../../../../store/generated/api.ts";
import {LicenseExpirationLabel} from "../../../shared/LicenseExpirationLabel";
import {LicenseStatusChip} from "../../../shared/status/LicenseStatusChip";
import {getTeamDisplayName} from "../../../../utils/teamUtils";

interface LicenseTableProps {
    licenses: VwpLicense[];
    onLicenseClick: (license: VwpLicense) => void;
}

export function LicenseTable({licenses, onLicenseClick}: LicenseTableProps) {
    const {t} = useTranslationWithKeys();

    const columns: ColumnDef<VwpLicense>[] = [
        {
            key: "product",
            header: t('admin.licenses.table.product'),
            cell: (license) => (
                <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-muted-foreground"/>
                    <span>{license.product}</span>
                    {license.trialStatus === "IN_TRIAL" && (
                        <Badge variant="warning" className="text-xs">
                            {t('licenses.trialStatus.inTrial')}
                        </Badge>
                    )}
                    {license.trialStatus === "TRIAL_COMPLETED" && (
                        <Badge variant="outline" className="text-xs">
                            {t('licenses.trialStatus.trialCompleted')}
                        </Badge>
                    )}
                    {license.supportLevel === 'SMA' && (
                        <Badge variant="secondary" className="text-xs">
                            {t('licenses.sma')}
                        </Badge>
                    )}
                    {license.supportLevel === 'SMA_PLUS' && (
                        <Badge variant="secondary" className="text-xs">
                            {t('licenses.smaPlus')}
                        </Badge>
                    )}
                </div>
            )
        },
        {
            key: "id",
            header: t('admin.licenses.table.id')
        },
        {
            key: "team",
            header: t('admin.licenses.table.team'),
            cell: (license) => {
                if (!license.team) {
                    return t('common.unassigned');
                }
                return getTeamDisplayName({
                    teamName: license.team.name,
                    firstName: license.team.adminName?.split(' ')[0],
                    lastName: license.team.adminName?.split(' ').slice(1).join(' ')
                }) ?? t('common.noTeam');
            }
        },
        {
            key: "quantity",
            header: t('admin.licenses.table.quantity')
        },
        {
            key: "assigned",
            header: t('admin.licenses.table.assigned'),
            cell: (license) => (
                <span className={license.assigned === license.quantity ? "text-orange-600" : ""}>
                    {license.assigned}/{license.quantity}
                </span>
            )
        },
        {
            key: "effectiveExpiryDate",
            header: t('admin.licenses.table.expires'),
            cell: (license) => <LicenseExpirationLabel license={license} tooltipMode="detailed"/>
        },
        {
            key: "status",
            header: t('admin.licenses.table.status'),
            cell: (license) => <LicenseStatusChip license={license}/>
        }
    ];

    return (
        <DataTable
            data={licenses}
            columns={columns}
            onRowClick={onLicenseClick}
            withBorder={false}
        />
    );
}